import {
  Component,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core'
import { IonicModule, IonInput } from '@ionic/angular'
import { AsyncPipe } from '@angular/common'
import { AssistanceEditorService } from '../assistance-editor.service'
import { AssistanceEditorModalService } from '../../assistance-editor-modal/assistance-editor-modal.service'
import { BehaviorSubject, groupBy, Subject } from 'rxjs'
import { debounceTime, map, mergeMap, switchMap, takeUntil, tap } from 'rxjs/operators'
import { AssistanceStatusesService } from '../../../../services/domain/codelists/assistance-statuses.service'
import { AssistanceStatus } from '../../../../models/assistanceStatus'
import { IRecommendedAssistantsFilter } from '../recommended-assistants-filter'
import { log } from '../../../../shared/utils/rxjsUtils'
import { DateRange } from '../../../../services/date-utils.service'
import { EditorAssistanceAssignment } from '../../../../models/EditorAssistance'
import { AssistanceEditorWorkerSelectService } from './assistance-editor-worker-select.service'
import { WorkerSelectItem } from '../AssistantSelectItem'

@Component({
  selector: 'app-assistance-editor-worker-select',
  templateUrl: './assistance-editor-worker-select.component.html',
  styleUrls: ['./assistance-editor-worker-select.component.scss'],
  standalone: true,
  host: {
    '[class.is-clickable]': 'isClickable',
  },
  imports: [IonicModule, AsyncPipe],
})
export class AssistanceEditorWorkerSelectComponent implements OnChanges, OnDestroy {
  private assistanceEditorService = inject(AssistanceEditorService)
  private assistanceEditorModalService = inject(AssistanceEditorModalService)
  private statusService = inject(AssistanceStatusesService)
  private workerSelectService = inject(AssistanceEditorWorkerSelectService)

  @Input() urid: string
  @Input() assistanceRange: DateRange
  @Input() disabled = false
  @Input() filterOverrides: Partial<IRecommendedAssistantsFilter> = {}

  @Output() uridChanged = new EventEmitter<string>()

  @ViewChildren('transitDurationInput')
  transitDurationInputs: QueryList<IonInput>

  @HostListener('click') async onClick() {
    if (this.isClickable) {
      await this.openWorkerSelect()
    }
  }

  private unsubscribe$$: Subject<void> = new Subject<void>()
  private urid$$: Subject<typeof this.urid> = new BehaviorSubject(null)
  private urid$ = this.urid$$.asObservable()
  private transitDurationChanged$ = new Subject<WorkerSelectItem>()

  protected assignments$$ = new BehaviorSubject<(EditorAssistanceAssignment & { status?: AssistanceStatus })[]>([])
  protected areAssignmentsEditable: boolean = true // Used to temporarily disable editing of assignments
  protected keepAssignmentEditable: number = null // ID of assignment to be left editable
  protected focusAssignmentId: number = null

  constructor() {
    this.urid$
      .pipe(
        log('AssistanceEditorWorkerSelectComponent new urid'),
        switchMap((urid) => this.assistanceEditorModalService.get(urid)),
        log('AssistanceEditorWorkerSelectComponent new editorAssistance'),
        map((editorAssistance) => editorAssistance?.assignments?.sort((a, b) => a.id - b.id) || []),
        log('AssistanceEditorWorkerSelectComponent new editorAssistanceAssignments'),
        switchMap((assignments) =>
          this.statusService.getAll().pipe(
            map((statuses) => {
              return assignments?.map((a) => {
                return {
                  ...a,
                  status: statuses.find((s) => s.id === a.statusId),
                }
              })
            }),
          ),
        ),
        takeUntil(this.unsubscribe$$),
      )
      .subscribe(this.assignments$$)

    this.assignments$$
      .pipe(
        tap(async () => {
          setTimeout(async () => {
            if (this.focusAssignmentId) {
              for (const input of this.transitDurationInputs) {
                if (input.name === 'assignment-' + this.focusAssignmentId + '-transit-duration') {
                  await input.setFocus()
                }
              }
            }
          }, 100)
        }),
        takeUntil(this.unsubscribe$$),
      )
      .subscribe()

    // See https://stackoverflow.com/a/58519798
    this.transitDurationChanged$
      .pipe(
        groupBy((change) => change.assignmentId),
        mergeMap((group) =>
          group.pipe(
            tap(({ assignmentId, transitDurationMinutes }) => {
              this.areAssignmentsEditable = false
              this.keepAssignmentEditable = assignmentId
            }),
            debounceTime(800),
            tap(async ({ assignmentId, transitDurationMinutes }) => {
              this.keepAssignmentEditable = null
              await this.workerSelectService.patchAssignment(assignmentId, {
                transitDurationMinutes,
              })
              this.areAssignmentsEditable = true
            }),
          ),
        ),
        takeUntil(this.unsubscribe$$),
      )
      .subscribe()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.urid && changes.urid.currentValue) {
      console.log(
        `AssistanceEditorWorkerSelectComponent#onChanges, will push next value`,
        this.urid$$,
        this.assignments$$,
        changes.urid.currentValue,
      )
      this.urid$$.next(changes.urid.currentValue)
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$$.next()
    this.unsubscribe$$.complete()
  }

  protected async onAssignmentNameClick($event) {
    if (!this.disabled) {
      await this.openWorkerSelect()
    }
  }

  async openWorkerSelect() {
    await this.assistanceEditorService.openWorkerSelect(
      this.urid,
      this.assistanceRange,
      (data) => {
        console.log('AssistanceEditorWorkerSelectComponent#openWorkerSelect dismiss callback', data)
        this.uridChanged.emit(data.assistanceURID)
      },
      this.filterOverrides,
    )
  }

  async onTransitDurationChange($event: any, assignmentId: number) {
    console.log('AssistanceEditorWorkerSelectComponent#onTransitDurationChange', $event, assignmentId)
    this.focusAssignmentId = assignmentId
    this.transitDurationChanged$.next({
      assignmentId,
      transitDurationMinutes: +$event.detail.value,
    })
  }

  get isClickable() {
    const assignments = this.assignments$$.getValue()
    return !assignments.length && !this.disabled
  }
}
